import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

/**
 * Design Library · Foundation → User Menu (node 1518:2 dark / 2224:4090 light).
 * Both nodes are structurally identical; only the resolved token values differ,
 * which is why one style module keyed on `colorScheme` covers both.
 */
const tokens = {
  dark: {
    surface: 'bg.card-elevated',
    divider: 'border.subtle',
    itemHoverBg: 'bg.table-row-hover',
    textPrimary: 'text.primary',
    textSecondary: 'text.secondary',
  },
  light: {
    surface: 'light.bg.card-elevated',
    divider: 'light.border.subtle',
    itemHoverBg: 'light.bg.table-row-hover',
    textPrimary: 'light.text.primary',
    textSecondary: 'light.text.secondary',
  },
} as const

const userMenuMetrics = {
  headerPaddingY: '12px',
  iconSize: 20,
  itemGap: '12px',
  itemHeight: '48px',
  itemPaddingX: '16px',
  itemPaddingY: '8px',
  itemTypography: {
    fontFamily: "'Soehne', sans-serif",
    fontSize: '15px',
    fontWeight: '400',
    lineHeight: '20px',
  },
  minWidth: '240px',
} as const

/** Reviewed width for the User Menu in the application navigation. */
export const USER_MENU_MIN_WIDTH = userMenuMetrics.minWidth

/** Leading icon box inside a User Menu row. */
export const USER_MENU_ICON_SIZE = userMenuMetrics.iconSize

export const userMenuSurfaceStyles = (
  colorScheme: ColorScheme = 'dark',
  minWidth?: number | string
): SystemStyleObject => ({
  alignItems: 'stretch',
  bg: tokens[colorScheme].surface,
  // Sharp four corners — radius/none is the default for every product surface.
  borderRadius: '0',
  boxShadow: 'elevated',
  display: 'flex',
  flexDirection: 'column',
  left: 0,
  minWidth: minWidth ?? USER_MENU_MIN_WIDTH,
  position: 'fixed',
  top: 0,
})

/**
 * The User Info block above the divider — name over role, with the same
 * horizontal gutter as the menu rows.
 */
export const userMenuHeaderStyles = (): SystemStyleObject => ({
  alignItems: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  px: '16px',
  py: userMenuMetrics.headerPaddingY,
  w: '100%',
})

export const userMenuNameStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].textPrimary,
  textStyle: 'body-large',
  wordBreak: 'break-word',
})

export const userMenuRoleStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].textSecondary,
  textStyle: 'caption',
  wordBreak: 'break-word',
})

export const userMenuDividerStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  bg: tokens[colorScheme].divider,
  flexShrink: 0,
  h: '1px',
  w: '100%',
})

export const userMenuItemStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  alignItems: 'center',
  bg: 'transparent',
  borderRadius: '0',
  color: tokens[colorScheme].textPrimary,
  cursor: 'pointer',
  display: 'flex',
  flexShrink: 0,
  gap: userMenuMetrics.itemGap,
  h: userMenuMetrics.itemHeight,
  px: userMenuMetrics.itemPaddingX,
  py: userMenuMetrics.itemPaddingY,
  textDecoration: 'none',
  w: '100%',
  ...userMenuMetrics.itemTypography,
  _hover: { bg: tokens[colorScheme].itemHoverBg },
  _focusVisible: { bg: tokens[colorScheme].itemHoverBg, outline: 'none' },
})

export const userMenuItemIconStyles = (): SystemStyleObject => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  h: `${USER_MENU_ICON_SIZE}px`,
  justifyContent: 'center',
  w: `${USER_MENU_ICON_SIZE}px`,
})

export const userMenuItemLabelStyles = (): SystemStyleObject => ({
  flex: '1 0 0',
  minWidth: 0,
  whiteSpace: 'nowrap',
})
